/**
 * Widget'ın client katmanı — "Elastic client" muadili.
 *
 * Çekirdek fetch mantığı panel ile paylaşılıyor (`lib/ai-search/query-client.ts`).
 * Bu dosya onun üstüne, **gömülü kullanımın gerektirdiği** davranışları koyar:
 *
 * - **Cache** — aynı sorgu tekrar kredi yakmaz (backspace, geri gelme).
 * - **Debounce** — tuş başına değil, yazma duraklamasında istek.
 * - **Abort** — yeni sorgu eskisini iptal eder, bayat sonuç ekrana basılmaz.
 * - **Minimum karakter** — anlamsız kısa sorgular hiç gitmez.
 * - **429 geri çekilmesi** — `Retry-After` boyunca istek atılmaz, çağıran
 *   mağazanın kendi aramasına düşebilir.
 *
 * Bunların hepsi maliyet ve dayanıklılık bilgisi; entegre eden geliştiricinin
 * bunları yeniden keşfetmesi gerekmesin.
 */

import {
  AiSearchQueryError,
  clampSearchLimit,
  DEFAULT_SEARCH_LIMIT,
  normalizeBaseUrl,
  runSearchQuery,
  runSearchServiceRequest,
  type AiSearchQueryResponse,
  type IAiSearchAutocompleteResponse,
  type IAiSearchFacetValuesResponse,
  type IAiSearchQueryResult,
} from "./search-core";

export const DEFAULT_BASE_URL = "https://aisearch.sendheap.com";
export const DEFAULT_MIN_CHARS = 3;
export const DEFAULT_DEBOUNCE_MS = 450;
export const DEFAULT_CACHE_SIZE = 50;

export interface ISearchClientOptions {
  /** Mağazanın arama anahtarı (64 hex). `x-search-key` başlığıyla gider. */
  searchKey?: string;
  /**
   * @deprecated `searchKey` kullanın. Eski `x-tenant` kurulumlarında verilen
   * değer aynı anahtar olduğu için takma ad olarak kabul ediliyor — mevcut
   * gömmeler widget güncellenince kendiliğinden çalışır.
   */
  tenant?: string;
  /**
   * Giriş yapmış müşterinin e-postası. Mağazada kişiselleştirilmiş arama
   * açıksa "Önerilen" sıralama ona göre düzenlenir.
   */
  email?: string;
  /** Servis kökü. Varsayılan prod host'u. */
  baseUrl?: string;
  /** Gösterilecek sonuç sayısı, 1–20 (servis sayfası istemcide kesilir). */
  limit?: number;
  /** Bu uzunluğun altındaki sorgular hiç gönderilmez. */
  minChars?: number;
  /** `watch()` içindeki bekleme süresi (ms). */
  debounce?: number;
  /** Cache'te tutulacak en fazla sorgu sayısı. */
  cacheSize?: number;
}

export interface ISearchOptions {
  limit?: number;
  signal?: AbortSignal;
  /** `true` ise cache atlanır ve servise gidilir (kredi harcar). */
  fresh?: boolean;
}

/** `watch()` çağıranına bildirilen durumlar. */
export type SearchState = "idle" | "loading" | "success" | "error";

export interface IWatchHandlers {
  onResults?: (payload: {
    query: string;
    results: IAiSearchQueryResult[];
    fromCache: boolean;
  }) => void;
  onError?: (error: AiSearchQueryError | Error) => void;
  onState?: (state: SearchState) => void;
}

/** `watchSuggest()` çağıranına bildirilenler — arama çubuğu yanıtı. */
export interface ISuggestHandlers {
  /** Kısa sorguda `data` null (servise gidilmedi). */
  onSuggest?: (payload: {
    query: string;
    data: IAiSearchAutocompleteResponse | null;
    fromCache: boolean;
  }) => void;
  onError?: (error: AiSearchQueryError | Error) => void;
  onState?: (state: SearchState) => void;
}

export interface IWatchHandle {
  /** Yeni sorgu metni ver — debounce edilir, yalnızca en sonu kazanır. */
  setQuery: (query: string) => void;
  /** Bekleyen debounce'u ve uçuştaki isteği iptal et. */
  cancel: () => void;
  /** Aboneliği bitir. */
  destroy: () => void;
}

export interface ISearchClient {
  /** Tek seferlik sorgu — cache'e bakar, debounce YOK. */
  search: (
    query: string,
    options?: ISearchOptions,
  ) => Promise<AiSearchQueryResponse>;
  /** Yazarken arama için abonelik (`/v1/search`, yalnız ürünler). */
  watch: (handlers: IWatchHandlers) => IWatchHandle;
  /** Tek seferlik arama çubuğu isteği (`/v1/autocomplete`) — cache'e bakar. */
  suggest: (
    query: string,
    options?: { signal?: AbortSignal },
  ) => Promise<IAiSearchAutocompleteResponse>;
  /**
   * Yazarken arama çubuğu aboneliği: öneriler, gruplar (marka/kategori…) ve
   * ürünler. Hazır arayüz bunu kullanır.
   */
  watchSuggest: (handlers: ISuggestHandlers) => IWatchHandle;
  /**
   * Yazmadan açılış verisi: boş sorgulu arama (`q=` → tüm ürünler, filtre
   * sayılarıyla). Örnek başına bir kez istenir, sonra bellekten gelir.
   */
  browse: () => Promise<AiSearchQueryResponse>;
  /**
   * Sonuç sayfası: sayfa, sıralama ve filtrelerle tam arama (`/v1/search`).
   * `params` servis parametreleridir (`q`, `page`, `sort`, `brand`, `price`,
   * `in_stock`…); aynı parametreler cache'ten gelir.
   */
  listing: (
    params: URLSearchParams,
    options?: { signal?: AbortSignal },
  ) => Promise<AiSearchQueryResponse>;
  /**
   * Filtre içinde arama (`/v1/facet-values`): `listing` parametreleri bağlam
   * olarak gider, sayılar o aramaya göre gelir.
   */
  facetValues: (
    field: string,
    needle: string,
    params: URLSearchParams,
    options?: { signal?: AbortSignal },
  ) => Promise<IAiSearchFacetValuesResponse>;
  /** Cache'i boşalt. */
  clearCache: () => void;
  /** Hız sınırı yüzünden şu an bloklu muyuz (ms cinsinden kalan süre, yoksa 0). */
  getRateLimitedForMs: () => number;
}

/**
 * Cache anahtarı: baştaki/sondaki boşluk, iç boşluk ve büyük/küçük harf önemsiz.
 *
 * **Türkçe İ tuzağı:** `"ELBİSE".toLowerCase()` → `"elbise"` DEĞİL, araya
 * U+0307 (combining dot above) koyan `"elbi̇se"` üretir. Bu yüzden büyük harfle
 * yazılan her Türkçe sorgu cache'i ıskalar ve boşuna kredi yakar. Locale'e bağlı
 * `toLocaleLowerCase("tr")` yerine yalnızca bu birleşen noktayı siliyoruz —
 * böylece ç/ş/ğ/ü/ö gibi harfler bozulmadan kalır (onları katlamak farklı
 * sorguları aynı anahtara düşürüp yanlış sonuç döndürürdü).
 */
const cacheKeyFor = (query: string, limit: number) =>
  `${query
    .trim()
    .toLowerCase()
    .replace(/\u0307/g, "")   // combining dot above
    .replace(/\s+/g, " ")}|${limit}`;

/** Kısa/boş sorguda servise gidilmeden dönen yanıt. */
const emptyResponse = (query: string): AiSearchQueryResponse => ({
  query,
  page: 1,
  per_page: 0,
  sort: "",
  sorts: [],
  intent: "none",
  intentLabel: "",
  found: 0,
  results: [],
  facets: [],
  suggestion: null,
  personalized: false,
  ms: 0,
});

export const createSearchClient = (
  options: ISearchClientOptions,
): ISearchClient => {
  const searchKey = options?.searchKey || options?.tenant;
  if (!searchKey) {
    throw new Error("[sendheap-search] `searchKey` zorunludur.");
  }

  const baseUrl = normalizeBaseUrl(options.baseUrl || DEFAULT_BASE_URL);
  const email = options.email || undefined;
  const defaultLimit = clampSearchLimit(options.limit ?? DEFAULT_SEARCH_LIMIT);
  const minChars = Math.max(0, options.minChars ?? DEFAULT_MIN_CHARS);
  const debounceMs = Math.max(0, options.debounce ?? DEFAULT_DEBOUNCE_MS);
  const cacheSize = Math.max(0, options.cacheSize ?? DEFAULT_CACHE_SIZE);

  /** Arama ve arama çubuğu yanıtları; anahtarlar önekle ayrışıyor. */
  const cache = new Map<string, unknown>();
  /** 429 sonrası bu zamana kadar hiç istek atılmaz (epoch ms). */
  let blockedUntil = 0;

  const readCache = <T>(key: string) => cache.get(key) as T | undefined;

  const writeCache = (key: string, value: unknown) => {
    if (cacheSize === 0) return;
    // Map ekleme sırasını koruduğu için en eskisi ilk anahtardır.
    if (cache.size >= cacheSize) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) cache.delete(oldest);
    }
    cache.set(key, value);
  };

  const remainingBlockMs = () => Math.max(0, blockedUntil - Date.now());

  const execute = async (
    query: string,
    { limit, signal, fresh }: ISearchOptions = {},
  ): Promise<AiSearchQueryResponse> => {
    const resolvedLimit = clampSearchLimit(limit ?? defaultLimit);
    const trimmed = query.trim();

    // Kısa/boş sorgu: servise hiç gitme — kredi ve gecikme yok.
    if (trimmed.length < minChars) return emptyResponse(trimmed);

    const key = cacheKeyFor(query, resolvedLimit);

    if (!fresh) {
      const cached = readCache<AiSearchQueryResponse>(key);
      if (cached) return cached;
    }

    // Hız sınırındayken istek atmak 429'u uzatmaktan başka işe yaramaz.
    const blocked = remainingBlockMs();
    if (blocked > 0) {
      throw new AiSearchQueryError(
        429,
        "rate_limit_exceeded",
        Math.ceil(blocked / 1000),
      );
    }

    try {
      const response = await runSearchQuery({
        baseUrl,
        searchKey,
        q: trimmed,
        limit: resolvedLimit,
        email,
        signal,
      });
      writeCache(key, response);
      return response;
    } catch (error) {
      if (error instanceof AiSearchQueryError && error.isRateLimited) {
        const waitSeconds = error.retryAfterSeconds ?? 10;
        blockedUntil = Date.now() + waitSeconds * 1000;
      }
      throw error;
    }
  };

  /** `search()` çağrıları arasında yalnızca en sonu geçerli — eskisi iptal edilir. */
  let oneShotController: AbortController | null = null;

  const search: ISearchClient["search"] = (query, searchOptions) => {
    if (searchOptions?.signal) return execute(query, searchOptions);

    oneShotController?.abort();
    const controller = new AbortController();
    oneShotController = controller;

    return execute(query, { ...searchOptions, signal: controller.signal });
  };

  /** Hız sınırındayken istek atma — `execute` ile aynı kural. */
  const throwIfBlocked = () => {
    const blocked = remainingBlockMs();
    if (blocked > 0) {
      throw new AiSearchQueryError(
        429,
        "rate_limit_exceeded",
        Math.ceil(blocked / 1000),
      );
    }
  };

  const rememberRateLimit = (error: unknown) => {
    if (error instanceof AiSearchQueryError && error.isRateLimited) {
      blockedUntil = Date.now() + (error.retryAfterSeconds ?? 10) * 1000;
    }
  };

  const suggestKeyFor = (query: string) => `ac|${cacheKeyFor(query, 0)}`;

  const executeSuggest = async (
    query: string,
    signal?: AbortSignal,
  ): Promise<IAiSearchAutocompleteResponse> => {
    const key = suggestKeyFor(query);
    const cached = readCache<IAiSearchAutocompleteResponse>(key);
    if (cached) return cached;
    throwIfBlocked();
    const params = new URLSearchParams({ q: query.trim() });
    if (email) params.set("email", email);
    try {
      const data = await runSearchServiceRequest<IAiSearchAutocompleteResponse>(
        { baseUrl, searchKey, path: "/v1/autocomplete", params, signal },
      );
      writeCache(key, data);
      return data;
    } catch (error) {
      rememberRateLimit(error);
      throw error;
    }
  };

  /**
   * Yazarken izleme iskeleti: debounce, abort, yarış koruması, cache isabetinde
   * anında yayın. `watch` (arama) ve `watchSuggest` (arama çubuğu) bunu paylaşır.
   */
  const createWatch = <T>(
    fetcher: (query: string, signal: AbortSignal) => Promise<T>,
    cachedFor: (query: string) => T | undefined,
    emit: (query: string, data: T | null, fromCache: boolean) => void,
    onError: ((error: AiSearchQueryError | Error) => void) | undefined,
    onState: ((state: SearchState) => void) | undefined,
  ): IWatchHandle => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    let destroyed = false;
    /** Yarış koşullarını kesmek için: yalnızca en son isteğin sonucu yayınlanır. */
    let requestSeq = 0;

    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const cancel = () => {
      clearTimer();
      controller?.abort();
      controller = null;
    };

    const run = (query: string) => {
      const seq = ++requestSeq;
      const trimmed = query.trim();

      // Cache isabeti: state'i loading'e hiç çevirmeden anında yayınla.
      const cached = trimmed.length >= minChars ? cachedFor(query) : undefined;
      if (cached) {
        onState?.("success");
        emit(trimmed, cached, true);
        return;
      }

      if (trimmed.length < minChars) {
        onState?.("idle");
        emit(trimmed, null, false);
        return;
      }

      controller?.abort();
      controller = new AbortController();
      onState?.("loading");

      fetcher(query, controller.signal)
        .then((data) => {
          if (destroyed || seq !== requestSeq) return;
          onState?.("success");
          emit(trimmed, data, false);
        })
        .catch((error: unknown) => {
          // Abort bir hata değil, yalnızca eskimiş istek.
          if (destroyed || seq !== requestSeq) return;
          if (error instanceof DOMException && error.name === "AbortError") return;
          onState?.("error");
          onError?.(error as AiSearchQueryError);
        });
    };

    return {
      setQuery: (query: string) => {
        if (destroyed) return;
        clearTimer();
        if (debounceMs === 0) {
          run(query);
          return;
        }
        timer = setTimeout(() => run(query), debounceMs);
      },
      cancel,
      destroy: () => {
        destroyed = true;
        cancel();
      },
    };
  };

  const watch: ISearchClient["watch"] = ({ onResults, onError, onState }) =>
    createWatch<AiSearchQueryResponse>(
      (query, signal) => execute(query, { signal }),
      (query) =>
        readCache<AiSearchQueryResponse>(cacheKeyFor(query, defaultLimit)),
      (query, data, fromCache) =>
        onResults?.({ query, results: data?.results ?? [], fromCache }),
      onError,
      onState,
    );

  const watchSuggest: ISearchClient["watchSuggest"] = ({
    onSuggest,
    onError,
    onState,
  }) =>
    createWatch<IAiSearchAutocompleteResponse>(
      (query, signal) => executeSuggest(query, signal),
      (query) =>
        readCache<IAiSearchAutocompleteResponse>(suggestKeyFor(query)),
      (query, data, fromCache) => onSuggest?.({ query, data, fromCache }),
      onError,
      onState,
    );

  let browsePromise: Promise<AiSearchQueryResponse> | null = null;
  const browse: ISearchClient["browse"] = () => {
    if (!browsePromise) {
      const params = new URLSearchParams({ q: "" });
      if (email) params.set("email", email);
      browsePromise = runSearchServiceRequest<AiSearchQueryResponse>({
        baseUrl,
        searchKey,
        path: "/v1/search",
        params,
      }).catch((error: unknown) => {
        // Başarısız açılış tekrar denenebilsin.
        browsePromise = null;
        rememberRateLimit(error);
        throw error;
      });
    }
    return browsePromise;
  };

  /**
   * Parametre sırası cache'i bölmesin: anahtar sıralanmış parametrelerden.
   * Kişiselleştirme e-postası istemciye ait, çağıranın parametresi değil.
   */
  const paramsKeyFor = (prefix: string, params: URLSearchParams) => {
    const sorted = new URLSearchParams(params);
    sorted.sort();
    return `${prefix}|${sorted.toString()}`;
  };

  const withEmail = (params: URLSearchParams) => {
    const next = new URLSearchParams(params);
    if (email) next.set("email", email);
    return next;
  };

  const requestCached = async <T>(
    key: string,
    path: string,
    params: URLSearchParams,
    signal?: AbortSignal,
  ): Promise<T> => {
    const cached = readCache<T>(key);
    if (cached) return cached;
    throwIfBlocked();
    try {
      const data = await runSearchServiceRequest<T>({
        baseUrl,
        searchKey,
        path,
        params: withEmail(params),
        signal,
      });
      writeCache(key, data);
      return data;
    } catch (error) {
      rememberRateLimit(error);
      throw error;
    }
  };

  const listing: ISearchClient["listing"] = (params, listingOptions) =>
    requestCached<AiSearchQueryResponse>(
      paramsKeyFor("ls", params),
      "/v1/search",
      params,
      listingOptions?.signal,
    );

  const facetValues: ISearchClient["facetValues"] = (
    field,
    needle,
    params,
    valuesOptions,
  ) => {
    const query = new URLSearchParams(params);
    query.delete("page");
    query.delete("sort");
    query.set("field", field);
    query.set("fq", needle.trim());
    return requestCached<IAiSearchFacetValuesResponse>(
      paramsKeyFor("fv", query),
      "/v1/facet-values",
      query,
      valuesOptions?.signal,
    );
  };

  return {
    search,
    watch,
    suggest: (query, options) => executeSuggest(query, options?.signal),
    watchSuggest,
    browse,
    listing,
    facetValues,
    clearCache: () => cache.clear(),
    getRateLimitedForMs: remainingBlockMs,
  };
};

export {
  AiSearchQueryError,
  type AiSearchQueryResponse,
  type IAiSearchAutocompleteResponse,
  type IAiSearchFacetValuesResponse,
  type IAiSearchQueryResult,
};
